import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { components } from "@/api/schema";

import { stockKeys } from "./query-keys";

type Marketplace = "shopee" | "lazada" | "tiktok";
type SyncResult = components["schemas"]["SyncResult"];

export interface SyncStockVars {
  sku: string;
  marketplace: Marketplace;
}

/**
 * Per-row on-demand stock sync (PRD v1.8 §4.6.3.2-C).
 *
 * Pushes the row's server-side `allocation_sellable` to the marketplace — the
 * same value the 5-minute batch job sends. The FE only triggers; the value itself is
 * computed server-side. This path is RATE-LIMITED per (sku × marketplace): a
 * throttled call returns HTTP 429 with `{ error: "rate_limited" }`, surfaced via
 * `ApiError(status=429)` by the client middleware so the caller can show the
 * "try again shortly" message.
 */
export function useSyncStock() {
  const queryClient = useQueryClient();
  return useMutation<SyncResult, Error, SyncStockVars>({
    mutationFn: async ({ sku, marketplace }) => {
      const { data, error } = await apiClient.POST("/v1/stock/{sku}/sync", {
        params: { path: { sku } },
        body: { marketplace },
      });
      if (error) throw error;
      return data;
    },
    onSettled: () => {
      // The push may move `last_synced_at`; refresh the list so it reflects.
      queryClient.invalidateQueries({ queryKey: stockKeys.lists });
    },
  });
}
